import React from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { makeStyles } from 'tss-react/mui';
import { Typography } from '@mui/material';
import Modal from '../../shared/Modal';
import Button from '../../shared/Button';
import { useLocales } from '../../../hooks';
import { useData } from '../../../data-layer';
import { CheckCircle, Error, Warning } from '@mui/icons-material';
import CountryBadge from '../../shared/CountryBadge';
import { bigSize } from '../../shared/Modal/Modal';
import { top10WarningModalTestIds } from '../../shared/TestsIds';
import { IdBadge } from '../../shared/IdBadge';
import { Top10ContentAvailabilityError } from '../../../API';
import Media from '../CollectionsPanel/Media';
import { DisplayAsOptions } from '../../../utils/types/genericTypes';

const useStyles = makeStyles()((theme) => ({
  modal: bigSize,
  modalTitle: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    gap: theme.spacing(3)
  },
  modalContent: {
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3)
  },

  errorSection: {
    backgroundColor: theme.palette.error.light + '10',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  contentErrorItem: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2)
  },
  contentTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  contentTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary
  },
  countriesList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  modalButtons: {
    display: 'flex',
    gap: theme.spacing(4)
  },
  collectionStats: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3)
  },
  collectionStat: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  usagePolicyText: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  contentInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  contentImageContainer: {
    width: 120
  }
}));

function Top10WarningModal(): JSX.Element {
  const { classes } = useStyles();
  const { t } = useLocales();

  const renderContentError = (error: Top10ContentAvailabilityError, index: number) => {
    return (
      <div key={index} className={classes.contentErrorItem}>
        <div className={classes.contentImageContainer}>
          <Media mediaId={error.contentId} displayAs={DisplayAsOptions.PORTRAIT} onContentPicked={() => false} />
        </div>
        <div className={classes.contentInfoContainer}>
          {error.contentTitle && (
            <div className={classes.contentTitleContainer}>
              <Typography variant="body1" className={classes.contentTitle}>
                {error.contentTitle}
              </Typography>
              <IdBadge id={error.contentId} />
            </div>
          )}
          {error.usagePolicy && (
            <div className={classes.contentTitleContainer}>
              <Typography variant="caption" component="span" className={classes.usagePolicyText}>
                {t('usage_policy.usage_policy')}: {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {t(`usage_policy.usage_policy_${(error.usagePolicy as any).default.toLowerCase()}`)}
              </Typography>
              <div className={classes.countriesList}>
                {error.usagePolicy.exceptionCountryCodes?.map((country: string, countryIndex: number) => (
                  <CountryBadge key={countryIndex} countryCode={country} />
                ))}
              </div>
            </div>
          )}
          {error.unavailableCountries && error.unavailableCountries.length > 0 && (
            <div>
              <Typography variant="caption" display="block" gutterBottom>
                {t('collections.top10_content_unavailable_in_countries')}
              </Typography>
              <div className={classes.countriesList}>
                {error.unavailableCountries.map((country: string, countryIndex: number) => (
                  <CountryBadge key={countryIndex} countryCode={country} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const {
    collections: {
      state: {
        withShowTop10WarningModal,
        withTop10ValidationWarnings,
        withShouldPublishCollectionWithWarnings,
        withIsSavingCollection
      }
    }
  } = useData();

  const [isOpen, setIsOpen] = useRecoilState(withShowTop10WarningModal);
  const [top10ValidationWarnings, setTop10ValidationWarnings] = useRecoilState(withTop10ValidationWarnings);
  const setShouldPublishCollection = useSetRecoilState(withShouldPublishCollectionWithWarnings);
  const isSavingCollection = useRecoilValue(withIsSavingCollection);

  // Calculate error counts based on Top10CollectionResponse structure
  const contentErrorsCount = top10ValidationWarnings?.contentErrors?.length || 0;
  const duplicateCountryErrorsCount = top10ValidationWarnings?.duplicateCountryErrors?.length || 0;
  const hasContentCountError = !!top10ValidationWarnings?.contentCountError;
  const hasCountryRequiredError = !!top10ValidationWarnings?.countryRequiredError;

  const totalErrorsCount =
    contentErrorsCount +
    duplicateCountryErrorsCount +
    (hasContentCountError ? 1 : 0) +
    (hasCountryRequiredError ? 1 : 0);
  const hasErrors = totalErrorsCount > 0;

  // Calculate validation types (4 total validation types: content, duplicates, count, country)
  const totalValidationTypes = 4;
  const failedValidationTypes =
    (contentErrorsCount > 0 ? 1 : 0) +
    (duplicateCountryErrorsCount > 0 ? 1 : 0) +
    (hasContentCountError ? 1 : 0) +
    (hasCountryRequiredError ? 1 : 0);
  const passedValidationTypes = totalValidationTypes - failedValidationTypes;

  const closeModal = () => {
    if (isSavingCollection) return;
    setIsOpen(false);
    setTop10ValidationWarnings(undefined);
    // Also reset the shouldPublishCollection flag to prevent stuck states
    setShouldPublishCollection(false);
  };

  const publishCollectionWithWarnings = () => {
    setShouldPublishCollection(true);
  };

  return (
    <Modal
      open={isOpen}
      bodyClassName={classes.modal}
      headerLeft={
        <div className={classes.modalTitle}>
          {hasErrors ? (
            <>
              <Error color="error" />
              <div>
                <Typography variant="body1" fontWeight={500}>
                  {t('collections.error_publishing_collection')}
                </Typography>
                <Typography variant="body2">{t('collections.top10_collection_has_errors')}</Typography>
              </div>
            </>
          ) : (
            <>
              <Warning color="warning" />
              <div>
                <Typography variant="body1" fontWeight={500}>
                  {t('collections.warning_publishing_collection')}
                </Typography>
                <Typography variant="body2">{t('collections.top10_collection_has_warnings')}</Typography>
              </div>
            </>
          )}
        </div>
      }
      footerRight={
        <div className={classes.collectionStats}>
          <div className={classes.collectionStat}>
            <Error fontSize="small" color="error" />{' '}
            <span data-testid={top10WarningModalTestIds.numberOfErrors}>{totalErrorsCount}</span> {t('general.errors')}
          </div>
          <div className={classes.collectionStat}>
            <CheckCircle fontSize="small" color="success" />{' '}
            <span data-testid={top10WarningModalTestIds.numberOfCorrect}>{passedValidationTypes}</span>{' '}
            {t('general.correct')}
          </div>
        </div>
      }
      footerLeft={
        <div className={classes.modalButtons}>
          {!hasErrors && (
            <Button
              size="small"
              onClick={publishCollectionWithWarnings}
              startIcon={<Warning fontSize="small" />}
              data-testid={top10WarningModalTestIds.publishBtn}
              loading={isSavingCollection}
            >
              {t('collections.publish_with_warnings')}
            </Button>
          )}
          <Button
            size="small"
            onClick={closeModal}
            color="grey"
            data-testid={top10WarningModalTestIds.cancelBtn}
            disabled={isSavingCollection}
          >
            {t('collections.close_and_fix')}
          </Button>
        </div>
      }
      data-testid={top10WarningModalTestIds.root}
      onClose={closeModal}
    >
      {top10ValidationWarnings ? (
        <div className={classes.modalContent}>
          {/* Content Errors */}
          {top10ValidationWarnings.contentErrors && top10ValidationWarnings.contentErrors.length > 0 && (
            <div className={classes.errorSection}>
              <div className={classes.sectionHeader}>
                <Error fontSize="small" color="error" />
                <Typography variant="h6" component="h3">
                  {t('collections.top10_content_errors')}
                </Typography>
                <Typography variant="caption" sx={{ ml: 1 }}>
                  ({top10ValidationWarnings.contentErrors.length} items)
                </Typography>
              </div>
              {top10ValidationWarnings.contentErrors.map((error: Top10ContentAvailabilityError, index: number) =>
                renderContentError(error, index)
              )}
            </div>
          )}

          {/* User Access Level Validation Errors (previously Duplicate Country Errors) */}
          {top10ValidationWarnings.duplicateCountryErrors &&
            top10ValidationWarnings.duplicateCountryErrors.length > 0 && (
              <div className={classes.errorSection}>
                <div className={classes.sectionHeader}>
                  <Error fontSize="small" color="error" />
                  <Typography variant="h6" component="h3">
                    {t('collections.top10_duplicate_country_errors')}
                  </Typography>
                  <Typography variant="caption" sx={{ ml: 1 }}>
                    ({top10ValidationWarnings.duplicateCountryErrors.length} {t('general.countries')})
                  </Typography>
                </div>
                <div className={classes.contentErrorItem}>
                  <div>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {t('collections.top10_user_access_level_info')}
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                      • {t('collections.top10_max_3_collections_per_country')}
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                      • {t('collections.top10_max_2_collections_per_country')}
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mb: 2 }}>
                      • {t('collections.top10_max_1_collection_per_country')}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                      {t('general.countries')}:
                    </Typography>
                    <div className={classes.countriesList}>
                      {top10ValidationWarnings.duplicateCountryErrors.map((error: string, index: number) => (
                        <CountryBadge key={`country-duplicated-${error}-${index}`} countryCode={error} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* Content Count Error */}
          {top10ValidationWarnings.contentCountError && (
            <div className={classes.errorSection}>
              <div className={classes.sectionHeader}>
                <Error fontSize="small" color="error" />
                <Typography variant="h6" component="h3">
                  {t('collections.top10_content_count_error')}
                </Typography>
              </div>
              <div className={classes.contentErrorItem}>
                <Typography variant="body2">
                  {typeof top10ValidationWarnings.contentCountError === 'string'
                    ? top10ValidationWarnings.contentCountError
                    : t('collections.top10_collection_must_have_10_contents')}
                </Typography>
              </div>
            </div>
          )}

          {/* Country Required Error */}
          {top10ValidationWarnings.countryRequiredError && (
            <div className={classes.errorSection}>
              <div className={classes.sectionHeader}>
                <Error fontSize="small" color="error" />
                <Typography variant="h6" component="h3">
                  {t('collections.top10_country_required_error')}
                </Typography>
              </div>
              <div className={classes.contentErrorItem}>
                <Typography variant="body2">{t('collections.top10_countries_required')}</Typography>
              </div>
            </div>
          )}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}

export default Top10WarningModal;
